import { Component, h, Host, Prop, Event, EventEmitter, Element, Watch, Method } from "@stencil/core";

@Component({
	tag: "smart-radio",
	styleUrl: "smart-radio.scss",
	shadow: true,
})

export class SmartRadio {
	@Element() el: HTMLSmartRadioElement;
	private nativeInput?: HTMLInputElement;
	private hiddenInput: HTMLInputElement;

	@Prop() identifier?: string;
	@Prop() label?: string;
	@Prop() name!: string;
	@Prop() value!: string;

	@Prop({ mutable: true }) checked?: boolean = false;
	@Prop({ reflect: false }) required?: boolean = false;
	@Prop({ reflect: false }) disabled?: boolean = false;
	// eslint-disable-next-line @stencil-community/strict-mutable
	@Prop({ mutable: true }) error = false;

	/** form#id - Associates the control with a form element. */
	@Prop() form?: string;

	@Prop() theme: "primary" | null = "primary";

	@Event() smartChange: EventEmitter<boolean>;

	@Watch("checked")
	updateHiddenInput() {
		if(this.hiddenInput) {
			this.hiddenInput.checked = this.checked;
			this.hiddenInput.value = this.value;
		}
	}

	/** Sets focus on the radio button. */
	@Method()
	async focusInner() {
		if (this.nativeInput !== undefined) {
			this.nativeInput.focus();
		}
	}

	private handleKeyDown = (ev: KeyboardEvent) => {
		if(ev.key === " ") {
			this.el.dispatchEvent(new InputEvent("click", { bubbles: true }));
		}
	};

	private handleClick = (ev: MouseEvent) => {
		if(this.disabled) {
			ev.preventDefault();
			return;
		}
		if(this.checked) return;

		Array.from(document.querySelectorAll("smart-radio"))
			.filter((smartRadio) => smartRadio.getAttribute("name") === this.name)
			.forEach((smartRadio: HTMLSmartRadioElement) => {
				smartRadio.checked = false;
			});
		
		this.checked = true;
		
		this.el.dispatchEvent(new InputEvent("change", { bubbles: true }));
		this.smartChange.emit(this.checked);

		// that is true when the event was generated by a user action, 
		// and false when the event was created or modified by a script or dispatched via EventTarget
		// if (!ev.isTrusted) {
		// this.el.dispatchEvent(new InputEvent('change', { bubbles: true }))
		// this.smartChange.emit(this.checked)
		// }
	};

	private handleChange = (event: InputEvent) => {
		this.el.dispatchEvent(new InputEvent("change", event));
		this.smartChange.emit(this.checked);
	};

	private createHiddenInput() {
		this.hiddenInput = document.createElement("input");
		this.hiddenInput.type = "radio";
		this.hiddenInput.style.visibility = "hidden";
		this.hiddenInput.style.position = "absolute";
		this.hiddenInput.style.pointerEvents = "none";
		this.el.appendChild(this.hiddenInput);
		this.hiddenInput.setAttribute("form", this.form);
	}

	componentWillLoad() {
		if (this.form && this.name) {
			this.createHiddenInput();
			this.hiddenInput.checked = this.checked;
			this.hiddenInput.name = this.name;

			if (this.value) {
				this.hiddenInput.value = this.value;
			}
		}
	}

	render() {
		const randomId: string = (this.name ? this.name : "rid") + new Date().getTime();
		const customId = this.identifier ?? randomId;

		return (
			<Host
				part="radio-element"
				role="radio"
				aria-checked={this.checked}
				aria-labelledby={`label-${customId}`}
				onKeyPress={this.handleKeyDown}
				onClick={this.handleClick}
				tabindex="0"
			>
				<div
					class={{
						"smart-radio": true,
						[`smart-radio--${this.theme}`]: true,
						"smart-radio--checked": this.checked,
						"smart-radio--disabled": this.disabled,
						"smart-radio--error": this.error
					}}
				>
					<input
						type="radio"
						id={customId}
						name={this.name}
						value={this.value}
						checked={this.checked}
						disabled={this.disabled}
						required={this.required}
						ref={ref => this.nativeInput = ref}
						onChange={this.handleChange}
						tabindex="-1"
					/>
					{this.label && (
						<label
							id={`label-${customId}`}
							htmlFor={customId}
						>
							<smart-text as="span" variant="subtitle-2">
								{this.label}
							</smart-text>
						</label>
					)}
				</div>
			</Host>
		);
	}
}
